import { UIIcon } from '../../config/icons';
import { YPaymentAmount } from '../y/amount';
import { YInput } from '../y/form';
import { $format } from '../y/format';
import { YTime, YDate } from '../y/time';
import { UIDateInput } from './component/date';
import { UITextInput, UINumberInput } from './component/input';
import { UICheckbox } from './component/toggle';
import { $el } from './elements';
import { UIAlignment } from './types';
import { UIViewAddSpec, UIViewSpec } from './view';

export interface UIValueFormat<Value> {
    alignment?: UIAlignment;
    content: (v: Value) => UIViewAddSpec | undefined;
    compare: (a: Value, b: Value, o: number) => number;
    edit?: () => (UIViewSpec & YInput<any>) | [UIViewSpec, YInput<any>];
}

export namespace $fmt {
    export const STRING: UIValueFormat<string> = {
        content: v => v,
        compare: (a, b, o) => o * a.localeCompare(b),  // a < b ? -o : b < a ? o : 0,
        edit: () => new UITextInput()
    }

    export const EMAIL: UIValueFormat<string> = {
        content: v => v,
        compare: (a, b, o) => a < b ? -o : b < a ? o : 0,
        edit: () => new UITextInput({ type: 'email' })
    }

    export const CURRENCY: UIValueFormat<YPaymentAmount | undefined> = {
        alignment: UIAlignment.Right,
        content: v => v == null ? '-' : $format.currencyFull(v),
        compare: (a, b, o) => o * ((a ? a.value : 0) - (b ? b.value : 0)),
        // edit: () => new UINumberInput
    }

    export const NUMBER: UIValueFormat<number> = {
        alignment: UIAlignment.Right,
        content: v => '' + v,
        compare: (a, b, o) => o * (a - b),
        edit: () => new UINumberInput
    }

    export const FILESIZE: UIValueFormat<number> = {
        alignment: UIAlignment.Right,
        content: v => $format.fileSize(v),
        compare: (a, b, o) => o * (a - b)
    }

    export const FULL_NAME: UIValueFormat<[string, string] | string[]> = {
        content: v => v[0] + ' ' + v[1],
        compare: (a, b, o) => o > 0 ? (a[0] < b[0] ? -1 : b[0] < a[0] ? 1 : 0) : (a[1] < b[1] ? -1 : b[1] < a[1] ? 1 : 0),
        edit: () => new UITextInput()
    }

    export function DATE(format: string = 'yyyy-MM-dd', empty: string = '-'): UIValueFormat<YTime | YDate | undefined> {
        return {
            content: v => v ? v.format(format) : empty,
            compare: (a, b, o) => o * ((a ? a.n : 0) - (b ? b.n : 0)),
            edit: () => new UIDateInput
        }
    }

    export function NUMBERMAX(n: number): UIValueFormat<number> {
        return {
            alignment: UIAlignment.Right,
            content: v => $format.maxNumber(v, n),
            compare: (a, b, o) => o * (a - b),
            edit: () => new UINumberInput
        }
    }

    export function OPTIONAL<T>(base: UIValueFormat<T>, null_content: UIViewAddSpec | undefined): UIValueFormat<T | undefined> {
        return {
            alignment: base.alignment,
            content: v => (v == undefined) ? null_content : base.content(v),
            compare: (a, b, o) => {
                if(a == undefined) {
                    if(b == undefined)
                        return 0;
                    else
                        return o;
                } else if(b == undefined) {
                    return -o;
                } else {
                    return base.compare(a, b, o);
                }
            },
            edit: base.edit
        }
    }

    export const PERCENTAGE: UIValueFormat<number | undefined> = {
        alignment: UIAlignment.Right,
        content: v => v == null ? '-' : (v * 100).toFixed(2) + '%',
        compare: (a, b, o) => o * ((a || 0) - (b || 0)),
        edit: () => new UINumberInput
    }

    export const DURATION: UIValueFormat<number | undefined> = {
        alignment: UIAlignment.Right,
        content: $format.duration,
        compare: (a, b, o) => o * ((a || 0) - (b || 0)),
        edit: () => new UINumberInput
    }

    export const CHECKMARK: UIValueFormat<boolean> = {
        alignment: UIAlignment.Center,
        content: v => v ? $el.ICON(UIIcon.CHECK) : undefined,
        compare: (a, b, o) => o * (+a - +b),
        edit: () => new UICheckbox
    }

    export function CHECKICON(icon: UIIcon): UIValueFormat<boolean> {
        return {
            alignment: UIAlignment.Center,
            content: v => v ? $el.ICON(icon) : undefined,
            compare: CHECKMARK.compare,
            edit: CHECKMARK.edit
        }
    }
}
